import {
    DateBookingRequestCheckoutState,
    GroupBookingRequestCheckoutState,
    BookingRequestDatesState,
} from '@shared/types';
import { Fragment, FunctionComponent, ReactNode, useMemo } from 'react';
import { CheckoutSummary, CheckoutSummaryProps } from '..';
import { useCostSummary } from '../../data/useCostSummary';
import styles from './styles.module.scss';
import { PriceSummary } from '../BookingCheckoutSummary/PriceSummary';
import { IconSummary } from '../IconSummary';
import { CalendarAltDuotoneIcon, UsersLightIcon } from '../../../Icons';
import { shortDateFormatOptions } from '../../../../services/dateFormats';
import { Cancellation } from '../Cancellation';
import { useCheckoutContext } from '../../CheckoutContext';
import { PublicBlendedCourse, PublicClassCourse } from '@cademy-package/sdk-component-types';
import { Temporal } from 'temporal-polyfill';
import { TimezoneDate } from '../../../TimezoneDate';

export type BookingRequestCheckoutSummaryProps = {
    course: PublicClassCourse | PublicBlendedCourse;
    checkoutState: DateBookingRequestCheckoutState | GroupBookingRequestCheckoutState;
};
export const BookingRequestCheckoutSummary: FunctionComponent<
    BookingRequestCheckoutSummaryProps
> = ({ course, checkoutState }) => {
    const { educator } = useCheckoutContext();
    const { offers: selectedOffers, addOns: selectedAddons } =
        useMemo((): BookingRequestDatesState => {
            if (checkoutState.stepState.dates.status === 'complete') {
                return checkoutState.stepState.dates.state;
            }
            return { offers: [], addOns: [], spacesRequested: 0, preferredDates: [] };
        }, [checkoutState]);
    const hidePricing = useMemo(
        () => checkoutState.origin !== 'marketplace' && educator.feature_flags?.hide_pricing,
        [checkoutState.origin, educator.feature_flags]
    );

    const { data: costSummary, isPreviousData: isLoadingCostSummary } = useCostSummary(
        {
            courseId: course._id,
            addons: selectedAddons,
            offers: selectedOffers,
            origin: checkoutState.origin,
        },
        {
            keepPreviousData: true,
        }
    );

    const spaces = useMemo((): ReactNode => {
        if (checkoutState.stepState.dates.status !== 'complete') {
            return null;
        }
        return (
            <IconSummary Icon={UsersLightIcon}>
                {checkoutState.stepState.dates.state.spacesRequested} spaces
            </IconSummary>
        );
    }, [checkoutState]);

    const enquiryTypeString = useMemo((): string => {
        switch (checkoutState.flow) {
            case 'dateBookingRequest':
                return 'availability for a specific date';
            case 'groupBookingRequest':
                return 'a group booking';
        }
    }, [checkoutState]);

    const preferredDates = useMemo((): ReactNode => {
        const potentiallyEmptyDates = checkoutState.stepState.dates.state?.preferredDates;
        if (!potentiallyEmptyDates || potentiallyEmptyDates.length === 0) {
            return null;
        }
        const dates = potentiallyEmptyDates.filter((date) => date !== '');
        if (dates.length === 0) {
            return null;
        }
        if (dates.length === 1) {
            return (
                <IconSummary Icon={CalendarAltDuotoneIcon}>
                    <TimezoneDate
                        date={Temporal.ZonedDateTime.from(dates[0])}
                        format={shortDateFormatOptions}
                    />
                </IconSummary>
            );
        }
        if (dates.length === 2) {
            return (
                <IconSummary Icon={CalendarAltDuotoneIcon}>
                    <TimezoneDate
                        date={Temporal.ZonedDateTime.from(dates[0])}
                        format={shortDateFormatOptions}
                    />{' '}
                    or{' '}
                    <TimezoneDate
                        date={Temporal.ZonedDateTime.from(dates[1])}
                        format={shortDateFormatOptions}
                    />
                </IconSummary>
            );
        }
        return (
            <IconSummary Icon={CalendarAltDuotoneIcon}>
                {dates.map((dateString, index) => {
                    if (!dateString) return null;
                    const date = Temporal.ZonedDateTime.from(dateString);
                    if (index === dates.length - 1) {
                        return (
                            <Fragment key={index}>
                                {' '}
                                or <TimezoneDate date={date} format={shortDateFormatOptions} />
                            </Fragment>
                        );
                    }
                    return (
                        <Fragment key={index}>
                            <TimezoneDate date={date} format={shortDateFormatOptions} />,{' '}
                        </Fragment>
                    );
                })}
            </IconSummary>
        );
    }, [checkoutState]);

    const detailsSection = useMemo((): ReactNode => {
        return (
            <>
                <span>Requested {enquiryTypeString}</span>
                {spaces}
                {preferredDates}
                <Cancellation educator={educator} course={course} />
            </>
        );
    }, [enquiryTypeString, spaces, preferredDates, educator, course]);

    const offersSection = useMemo((): ReactNode => {
        if (!costSummary || hidePricing) {
            return null;
        }
        const { offerSummary, addOnSummary } = costSummary.summary_items;
        if (!offerSummary.length && !addOnSummary.length) {
            return null;
        }
        return (
            <ul className={styles.costSection}>
                {offerSummary.map((offer, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={offer.text}
                                price={offer.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
                {addOnSummary.map((addon, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={addon.text}
                                price={addon.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
            </ul>
        );
    }, [costSummary, course.currency, hidePricing]);

    const discountsAndVouchersSection = useMemo((): ReactNode => {
        if (!costSummary) {
            return null;
        }
        const { discountSummary, voucherSummary } = costSummary.summary_items;
        if (!discountSummary.length && !voucherSummary.length) {
            return null;
        }
        return (
            <ul className={styles.costSection}>
                {discountSummary.map((discount, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={discount.text}
                                price={-discount.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
                {voucherSummary.map((voucher, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={voucher.text}
                                price={-voucher.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
            </ul>
        );
    }, [costSummary, course.currency]);

    const additionalChargesSection = useMemo((): ReactNode => {
        if (!costSummary || hidePricing) {
            return null;
        }
        const { vatSummary } = costSummary.summary_items;
        const { cademy_fees, fees_absorbed } = costSummary;
        if (!vatSummary.length && (!cademy_fees || fees_absorbed === true)) {
            return null;
        }
        return (
            <ul className={styles.costSection}>
                {vatSummary.map((vat, index) => {
                    return (
                        <li key={index}>
                            <PriceSummary
                                label={vat.text}
                                price={vat.value}
                                currency={course.currency}
                            />
                        </li>
                    );
                })}
                {cademy_fees && fees_absorbed !== true ? (
                    <li>
                        <PriceSummary label="Fees" price={cademy_fees} currency={course.currency} />
                    </li>
                ) : null}
            </ul>
        );
    }, [costSummary, course.currency, hidePricing]);

    const totalSection = useMemo((): ReactNode => {
        if (!costSummary || hidePricing) {
            return null;
        }
        return (
            <PriceSummary
                className={styles.totalSummary}
                label="Total"
                price={costSummary.total_cost}
                currency={course.currency}
            />
        );
    }, [costSummary, course.currency, hidePricing]);

    const sections = useMemo((): CheckoutSummaryProps['summarySections'] => {
        return [
            detailsSection,
            offersSection,
            discountsAndVouchersSection,
            additionalChargesSection,
            totalSection,
        ];
    }, [
        detailsSection,
        offersSection,
        discountsAndVouchersSection,
        additionalChargesSection,
        totalSection,
    ]);

    return (
        <CheckoutSummary
            course={course}
            taglineDetail={detailsSection}
            summarySections={sections}
            totalCost={costSummary?.total_cost}
        />
    );
};
